import consola from "consola"
import { events } from "fetch-event-stream"

import { copilotHeaders, copilotBaseUrl } from "~/lib/api-config"
import { HTTPError } from "~/lib/error"
import { UPSTREAM_FETCH_TIMEOUT_MS } from "~/lib/port"
import { MAX_RESPONSE_BODY_BYTES, readResponseBodyCapped } from "~/lib/response-cap"
import { state } from "~/lib/state"
import { tryRefreshAndRetry } from "~/lib/token"

export const createChatCompletions = async (
  payload: ChatCompletionsPayload,
  modelHeaders?: Record<string, string>,
  callerSignal?: AbortSignal,
) => {
  if (!state.copilotToken) throw new Error("Copilot token not found")

  const enableVision = payload.messages.some(
    (x) =>
      typeof x.content !== "string"
      && x.content?.some((x) => x.type === "image_url"),
  )

  // Agent/user check for X-Initiator header
  // Determine if any message is from an agent ("assistant" or "tool")
  const isAgentCall = payload.messages.some((msg) =>
    ["assistant", "tool"].includes(msg.role),
  )

  const url = `${copilotBaseUrl(state)}/chat/completions`
  const doFetch = (): Promise<Response> => {
    // Re-build headers per attempt so a 401-retry picks up the refreshed token.
    const headers: Record<string, string> = {
      ...copilotHeaders(state, enableVision),
      ...modelHeaders,
      "X-Initiator": isAgentCall ? "agent" : "user",
    }
    const fetchInit: RequestInit = {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    }
    const signals: Array<AbortSignal> = []
    if (UPSTREAM_FETCH_TIMEOUT_MS > 0) {
      signals.push(AbortSignal.timeout(UPSTREAM_FETCH_TIMEOUT_MS))
    }
    if (callerSignal) signals.push(callerSignal)
    if (signals.length === 1) fetchInit.signal = signals[0]
    else if (signals.length > 1) fetchInit.signal = AbortSignal.any(signals)
    return fetch(url, fetchInit)
  }
  const response = await tryRefreshAndRetry(doFetch, "/chat/completions")

  if (!response.ok) {
    let errorBody = ""
    try {
      errorBody = await response.text()
    } catch {
      errorBody = "(could not read error body)"
    }
    const claudeModels = state.models?.data
      .filter((m) => m.id.startsWith("claude"))
      .map((m) => m.id)
      .join(", ") ?? "(models not loaded)"
    consola.error(
      `Copilot rejected model "${payload.model}": ${response.status} ${errorBody} (available Claude models: ${claudeModels})`,
    )
    // Re-create the response so downstream error handlers can still read the body
    const reconstructed = new Response(errorBody, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    })
    throw new HTTPError("Failed to create chat completions", reconstructed)
  }

  if (payload.stream) {
    return events(response)
  }

  const cappedResult = await readResponseBodyCapped<ChatCompletionResponse>(
    response,
    "/v1/chat/completions",
    MAX_RESPONSE_BODY_BYTES,
  )
  if (!cappedResult.ok) {
    throw new HTTPError(
      "Upstream /v1/chat/completions response exceeded 10 MiB size cap",
      new Response(JSON.stringify(cappedResult.errorResponse), {
        status: cappedResult.status,
        headers: { "content-type": "application/json" },
      }),
    )
  }
  return cappedResult.value
}

// Streaming types

export interface ChatCompletionChunk {
  id: string
  object: "chat.completion.chunk"
  created: number
  model: string
  choices: Array<Choice>
  system_fingerprint?: string
  usage?: {
    prompt_tokens: number
    completion_tokens: number
    total_tokens: number
    prompt_tokens_details?: {
      cached_tokens: number
    }
    completion_tokens_details?: {
      accepted_prediction_tokens: number
      rejected_prediction_tokens: number
    }
  }
}

interface Delta {
  content?: string | null
  role?: "user" | "assistant" | "system" | "tool"
  tool_calls?: Array<{
    index: number
    id?: string
    type?: "function"
    function?: {
      name?: string
      arguments?: string
    }
  }>
}

interface Choice {
  index: number
  delta: Delta
  finish_reason: "stop" | "length" | "tool_calls" | "content_filter" | null
  logprobs: object | null
}

// Non-streaming types

export interface ChatCompletionResponse {
  id: string
  object: "chat.completion"
  created: number
  model: string
  choices: Array<ChoiceNonStreaming>
  system_fingerprint?: string
  usage?: {
    prompt_tokens: number
    completion_tokens: number
    total_tokens: number
    prompt_tokens_details?: {
      cached_tokens: number
    }
  }
}

interface ResponseMessage {
  role: "assistant"
  content: string | null
  tool_calls?: Array<ToolCall>
}

interface ChoiceNonStreaming {
  index: number
  message: ResponseMessage
  logprobs: object | null
  finish_reason: "stop" | "length" | "tool_calls" | "content_filter"
}

// Payload types

export interface ChatCompletionsPayload {
  messages: Array<Message>
  model: string
  temperature?: number | null
  top_p?: number | null
  max_tokens?: number | null
  stop?: string | Array<string> | null
  n?: number | null
  stream?: boolean | null

  frequency_penalty?: number | null
  presence_penalty?: number | null
  logit_bias?: Record<string, number> | null
  logprobs?: boolean | null
  response_format?: { type: "json_object" } | null
  seed?: number | null
  tools?: Array<Tool> | null
  tool_choice?:
    | "none"
    | "auto"
    | "required"
    | { type: "function"; function: { name: string } }
    | null
  user?: string | null
  /**
   * OpenAI-compatible reasoning effort knob. Copilot accepts low/medium/high/xhigh
   * for OpenAI-routed models; for non-OpenAI models (e.g. gemini-3.x routed via
   * /v1/chat/completions) the upstream may silently ignore this or 400 — the proxy
   * forwards it as-is and surfaces any 400 through the existing tool-error path.
   */
  reasoning_effort?: string | null
}

export interface Tool {
  type: "function"
  function: {
    name: string
    description?: string
    parameters: Record<string, unknown>
  }
}

export interface Message {
  role: "user" | "assistant" | "system" | "tool" | "developer"
  content: string | Array<ContentPart> | null

  name?: string
  tool_calls?: Array<ToolCall>
  tool_call_id?: string
}

export interface ToolCall {
  id: string
  type: "function"
  function: {
    name: string
    arguments: string
  }
}

export type ContentPart = TextPart | ImagePart

export interface TextPart {
  type: "text"
  text: string
}

export interface ImagePart {
  type: "image_url"
  image_url: {
    url: string
    detail?: "low" | "high" | "auto"
  }
}
